import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, Plus, Pencil, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";

export const upiUri = (o) => o?.upi_id ? `upi://pay?pa=${encodeURIComponent(o.upi_id)}&pn=${encodeURIComponent(o.upi_name || o.name || "")}&cu=INR` : "";

export default function Owners() {
  const [items, setItems] = useState([]);
  const [tenantCount, setTenantCount] = useState({});
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);
  const { can } = useAuth();

  const load = async () => {
    try {
      const [os, ts] = await Promise.all([api.get("/owners"), api.get("/tenants").catch(() => [])]);
      setItems(os);
      const counts = {};
      for (const t of ts) counts[t.owner_id] = (counts[t.owner_id] || 0) + 1;
      setTenantCount(counts);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = async () => {
    setEdit(null);
    let code = "";
    try { code = (await api.get("/next-code/owners")).code; } catch {}
    setForm({ code });
    setOpen(true);
  };
  const openEdit = (o) => { setEdit(o); setForm({ ...o }); setOpen(true); };
  const pickImage = (e, key) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image file (PNG or JPG)");
    if (f.size > 400 * 1024) return toast.error("Please pick an image under 400 KB");
    const r = new FileReader();
    r.onload = () => setForm((prev) => ({ ...prev, [key]: r.result }));
    r.readAsDataURL(f);
  };
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Business / Owner name is required");
    try {
      if (edit) await api.put(`/owners/${edit.id}`, form);
      else await api.post("/owners", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async () => {
    const used = tenantCount[del.id] || 0;
    if (used) return toast.error(`Cannot delete — ${used} tenant(s) linked. Reassign them first.`);
    try { await api.del(`/owners/${del.id}`); toast.success("Deleted"); setDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const canEdit = can("owners.edit");

  return (
    <div data-testid="owners-page">
      <PageHeader title="Business / Owner Names" subtitle={`${items.length} entr${items.length === 1 ? "y" : "ies"} on file`} testid="owners-header">
        {can("owners.create") && <Button data-testid="add-owner-btn" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" /> Add Business / Owner</Button>}
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No business or owner names yet" hint="Add every business name / owner whose properties you manage. You'll link each tenant to one of them." action={canEdit && <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700">Add Business / Owner</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Code</TableHead><TableHead>Business / Owner Name</TableHead><TableHead>PAN / GSTIN</TableHead><TableHead>Contact</TableHead><TableHead>Bank</TableHead><TableHead className="text-center">Payment QR</TableHead><TableHead className="text-center">Tenants</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`owner-row-${o.code || o.id}`}>
                  <TableCell className="font-mono text-xs">{o.code}</TableCell>
                  <TableCell><div className="flex items-center gap-2">{o.logo_image && <img src={o.logo_image} alt="" className="h-6 w-6 object-contain rounded" data-testid={`owner-logo-cell-${o.id}`} />}<div><div className="font-medium text-slate-800">{o.name}</div>{o.legal_type && <div className="text-xs text-slate-400">{o.legal_type}</div>}</div></div></TableCell>
                  <TableCell className="text-xs font-mono">{o.pan || "-"}{o.gstin ? <><br />{o.gstin}</> : ""}</TableCell>
                  <TableCell className="text-xs">
                    {o.mobile && <div className="inline-flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{o.mobile}</div>}
                    {o.email && <div className="inline-flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" />{o.email}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{o.bank_name || "-"}{o.bank_account ? ` · ${o.bank_account}` : ""}</TableCell>
                  <TableCell className="text-center">
                    {o.upi_id ? <div className="inline-flex flex-col items-center gap-1" data-testid={`owner-qr-${o.id}`}><QRCodeCanvas value={upiUri(o)} size={48} /><span className="text-[10px] font-mono text-slate-500">{o.upi_id}</span></div> : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{tenantCount[o.id] || 0}</span></TableCell>
                  <TableCell className="text-right">
                    {canEdit && <Button size="sm" variant="ghost" title="Edit owner" onClick={() => openEdit(o)} data-testid={`edit-owner-${o.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {can("owners.delete") && <Button size="sm" variant="ghost" className="text-rose-600" title="Delete owner" onClick={() => setDel(o)} data-testid={`delete-owner-${o.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Edit business / owner" : "Add business / owner"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code"><Input data-testid="owner-code" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!edit} /></Field>
            <Field label="Legal Type">
              <select data-testid="owner-legal-type" value={form.legal_type || "Individual"} onChange={(e) => setForm({ ...form, legal_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                {["Individual","Proprietorship","Partnership","LLP","Private Limited","HUF","Trust","Other"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <div className="col-span-2"><Field label="Business / Owner Name *"><Input data-testid="owner-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Leo Realty Estates" /></Field></div>
            <Field label="Contact Person"><Input data-testid="owner-contact" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
            <Field label="Mobile"><Input data-testid="owner-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Field>
            <Field label="Email"><Input data-testid="owner-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="PAN"><Input data-testid="owner-pan" value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} /></Field>
            <Field label="GSTIN (optional)"><Input data-testid="owner-gstin" value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></Field>
            <Field label="Bank Name"><Input data-testid="owner-bank-name" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Field>
            <Field label="Bank Account No."><Input data-testid="owner-bank-account" value={form.bank_account || ""} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></Field>
            <Field label="IFSC"><Input data-testid="owner-ifsc" value={form.bank_ifsc || ""} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} /></Field>
            <Field label="UPI ID (for payment QR)"><Input data-testid="owner-upi-id" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value.trim() })} placeholder="name@okhdfcbank" /></Field>
            <Field label="UPI Payee Name"><Input data-testid="owner-upi-name" value={form.upi_name || ""} onChange={(e) => setForm({ ...form, upi_name: e.target.value })} placeholder="Shown in the tenant's UPI app" /></Field>
            {form.upi_id && (
              <div className="col-span-2 flex items-center gap-4 p-3 rounded-md border border-emerald-200 bg-emerald-50/60" data-testid="owner-qr-preview">
                <div className="bg-white p-2 rounded border border-emerald-200"><QRCodeCanvas value={upiUri(form)} size={104} /></div>
                <div className="text-xs text-slate-600">
                  <p className="font-semibold text-emerald-800 mb-1">Payment QR preview</p>
                  <p>Auto-generated from the UPI ID. Tenants of this business / owner will see this QR in the portal and on their invoice PDF.</p>
                </div>
              </div>
            )}
            <div className="col-span-2 grid grid-cols-2 gap-3">
              {[["logo_image","Logo (on invoice header)","owner-logo"],["signature_image","Signature (on invoice footer)","owner-signature"]].map(([key,label,tid]) => (
                <div key={key} className="rounded-md border border-slate-200 p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">{label}</p>
                  {form[key] && <img src={form[key]} alt={label} className="h-12 object-contain mb-2" data-testid={`${tid}-preview`} />}
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/*" data-testid={`${tid}-input`} className="text-xs" onChange={(e) => pickImage(e, key)} />
                    {form[key] && <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`${tid}-clear`} onClick={() => setForm({ ...form, [key]: "" })}>Remove</Button>}
                  </div>
                </div>
              ))}
            </div>
            <div className="col-span-2"><Field label="Address"><Textarea data-testid="owner-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
            <div className="col-span-2"><Field label="Notes (optional)"><Textarea data-testid="owner-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else worth remembering about this business / owner" /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-owner-btn" onClick={save} className="bg-indigo-600 hover:bg-indigo-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this business / owner?</AlertDialogTitle><AlertDialogDescription>This soft-deletes "{del?.name}". Tenants must be reassigned before deletion.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
